import React, { useCallback, useState } from 'react'
import { truncateName } from '../../utils/dataUtils';
import { Tooltip } from '@jobtarget/ui-library';
import { DeleteReason } from '../../types/interview';
import DeletedMediaPlaceholder from './DeletedMediaPlaceholder';

interface VideoCardProps {
  applicantName?: string;
  jobTitle?: string;
  dateApplied?: string;
  thumbnailUrl?: string;
  onClick?: () => void;
  isMobile?: boolean;
  isLoading?: boolean;
  videoId?: string;
  compact?: boolean;
  isSelected?: boolean;
  // VI-468: when the interview's media has been deleted, render the
  // frontend-only placeholder + reason-driven copy in place of the
  // thumbnail/play UI.
  isDeleted?: boolean;
  deleteReason?: DeleteReason | null;
}

const VideoCard: React.FC<VideoCardProps> = ({ 
  applicantName = "",
  jobTitle = "", 
  dateApplied = "",
  thumbnailUrl = "",
  onClick,
  isMobile=false,
  isLoading = false,
  videoId,
  compact = false,
  isSelected = false,
  isDeleted = false,
  deleteReason = null
}) => {
  const smallerDesktops = !isMobile && window.innerWidth <= 1280
  const shouldDisplayNameTooltip = applicantName?.length > (compact && window.innerWidth < 768 ? 8 : 20)
  const shouldDisplayjobTitleTooltip = isMobile ? jobTitle?.length > 50 : jobTitle?.length > 32
  const [isHovered, setIsHovered] = useState(false);

  // Memoize hover handlers to prevent recreating functions
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  if (isLoading) {
    return (
      <div
        style={{
          // border: '1px solid #e0e0e0',
          borderRadius: '12px',
          backgroundColor: '#f8f9fa',
          width: '100%',
          height: isMobile || smallerDesktops ? 'auto' : '100%',
          padding: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {/* Custom Skeleton Thumbnail */}
        <div
          style={{
            width: '100%',
            height: '212px',
            borderRadius: '8px',
            backgroundColor: '#e9ecef',
            animation: 'pulse 1.5s ease-in-out infinite alternate'
          }}
        />
  
        {/* Custom Skeleton Metadata */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : undefined,
            gap: '0.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.3rem',
              flex: 1
            }}
          >
            {/* Skeleton applicant name */}
            <div style={{ 
              width: '70%', 
              height: '20px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite alternate'
            }}></div>
            {/* Skeleton job title */}
            <div style={{ 
              width: '85%', 
              height: '16px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite alternate'
            }}></div>
          </div>
  
          {/* Skeleton date */}
          <div style={{ 
            width: '60px', 
            height: '14px',
            backgroundColor: '#e9ecef', 
            borderRadius: '4px',
            marginTop: 'auto',
            marginLeft: isMobile ? undefined : 'auto',
            animation: 'pulse 1.5s ease-in-out infinite alternate'
          }}></div>
        </div>
  
        <style>{`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
        style={{
            border: (compact && isSelected) ? '2px solid #2F86DD' : '1px solid #e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#f8f9fa',
            width: '100%',
            height: compact && !isMobile? '80%' : (isMobile ? 'auto' : '100%'),
            padding: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            cursor: 'pointer',
            transition: compact ? 'none' : 'transform 0.2s ease, box-shadow 0.2s ease',
            transform: compact ? 'none' : (isHovered && onClick ? 'translateY(-2px)' : 'none'),
            boxShadow: compact ? 'none' : (isHovered && onClick ? '0 4px 12px rgba(0,0,0,0.1)' : 'none')
        }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
    >
        {/* Video Thumbnail */}
        <div
            style={{
                width: '100%',
                // aspectRatio: compact ? '16/9' : undefined,
                aspectRatio: '16/9', // Force 16:9 aspect ratio
                height: compact ? 'auto' : (isMobile || smallerDesktops ? 'auto' : '212px'),
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#f8f9fa',
                flex: compact ? 1 : 'none'
            }}
        >
            {isDeleted ? (
              // VI-468: deleted screener — render the placeholder UI in
              // place of the thumbnail+play overlay. Card stays clickable
              // so the recruiter can still open the overlay modal.
              <DeletedMediaPlaceholder variant="card" deleteReason={deleteReason} />
            ) : (
              <>
                <img
                    src={thumbnailUrl}
                    alt={`${applicantName} screener thumbnail`}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/images/audio-placeholder.jpg'; }}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                />

                {/* Black/Shadow overlay to indicate not playing */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: isHovered ? 'transparent' : 'rgba(0, 0, 0, 0.4)', // Semi-transparent black overlay
                    transition: 'background-color 0.2s ease'
                  }}
                />

                {/* Play button overlay */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* <img
                    src="/svgs/play-button.svg"
                    alt="Play video"
                    width="24"
                    height="24"
                    /> */}
                    <i
                      className="fas fa-play"
                      style={{
                        color: 'white',
                        opacity: 0.75,
                        fontSize: '28px', // This controls the size for FontAwesome icons
                        width: '28px',
                        height: '32x',
                        display: 'inline-block'
                      }}
                    ></i>
                </div>
              </>
            )}
        </div>

        {/* Video Metadata */}
        <div style={{
            display: 'flex',
            flexDirection: compact ? (isMobile ? 'column' : 'row') : (isMobile ? 'column' : undefined),
            gap: compact ? '0.25rem' : '0.5rem', // Smaller gap for compact
            flexShrink: 0, // Add this
            minHeight: 'auto',
            // backgroundColor:'blue'
        }}>
            <div 
              style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: compact ? '0.1rem' : '0.3rem', // Smaller gap for compact
              }}
            >
                {shouldDisplayNameTooltip ? (<Tooltip message={applicantName} position="top">
                  <span style={{ 
                    fontWeight: '600', 
                    fontSize: compact ? '0.75rem' : '0.95rem', // Smaller text for compact
                    color: '#1a1a1a',
                    // backgroundColor:'red'
                }}>
                  {truncateName(applicantName, compact && window.innerWidth < 768 ? 8 : 20)}
                </span>
                </Tooltip>) : (
                  <span style={{ 
                    fontWeight: '600', 
                    fontSize: compact ? '0.75rem' : '0.95rem', // Smaller text for compact
                    color: '#1a1a1a',
                    // backgroundColor:'red'
                }}>
                  {truncateName(applicantName, compact && window.innerWidth < 768 ? 8 : 20)}
                </span>
                )}
                {jobTitle && !compact && (
                  shouldDisplayjobTitleTooltip ? (<Tooltip message={jobTitle} position="top">
                    <span style={{ 
                        fontSize: '0.85rem', // Smaller text for compact
                        color: '#6c757d' 
                    }}>
                      Applied for <strong>{truncateName(jobTitle, isMobile ? 50 : smallerDesktops ? 24 : 32)}</strong>
                    </span>
                  </Tooltip>) : (
                    <span style={{ 
                      fontSize: '0.85rem', // Smaller text for compact
                      color: '#6c757d' 
                    }}>
                      Applied for <strong>{truncateName(jobTitle, isMobile ? 50 : smallerDesktops ? 24 : 32)}</strong>
                    </span>
                  )
                )}
            </div>

            <span style={{
                fontSize: compact ? '0.65rem' : '0.8rem', // Smaller text for compact
                color: '#9ca3af',
                marginTop: compact ? 0 : 'auto',
                marginLeft: compact && isMobile ? 0 : (compact ? 'auto' : (isMobile ? undefined : 'auto')), // Leftmost when mobile and compact
                flexShrink: 0,
                // backgroundColor:'red',
                // lineHeight: compact ? '1' : 'normal', // Reduce line height in compact
                alignSelf: compact && isMobile ? 'flex-start' : (compact ? 'center' : 'auto'), // Leftmost when mobile and compact
                height: compact ? 'fit-content' : 'auto',
            }}>
              {dateApplied}
            </span>
        </div>
    </div>
  )
}

export default VideoCard